import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle, Clock, Mail, Star, Loader2, ThumbsUp } from "lucide-react";

export default function InterviewComplete() {
  const [location] = useLocation();
  const [token, setToken] = useState<string | null>(null);

  const [rating, setRating] = useState<number>(0);
  const [hovered, setHovered] = useState<number>(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("t");
    if (t) setToken(t);
  }, [location]);

  const submitFeedback = async () => {
    if (!token || rating === 0) return;
    setSubmitting(true);
    setFeedbackError(null);
    try {
      const res = await fetch(`/api/invites/${encodeURIComponent(token)}/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rating, comments: comment.trim() || undefined }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || `Failed (${res.status})`);
      }
      if (data.alreadySubmitted) {
        setAlreadySubmitted(true);
      } else {
        setSubmitted(true);
      }
    } catch (err: any) {
      setFeedbackError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-lg w-full p-8 text-center cyber-border">
        <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-10 h-10 text-primary" />
        </div>

        <h1 className="text-3xl font-bold mb-4">Interview Complete!</h1>

        <p className="text-lg text-muted-foreground mb-8">
          Thank you for completing your interview. Our AI is now analyzing your responses.
        </p>

        <div className="space-y-4 text-left mb-8">
          <div className="flex items-start gap-4 p-4 bg-muted/30 rounded-lg">
            <Clock className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold">What happens next?</h3>
              <p className="text-sm text-muted-foreground">
                Your responses are being analyzed for competency, communication, and role fit.
                This typically takes just a few minutes.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4 p-4 bg-muted/30 rounded-lg">
            <Mail className="w-6 h-6 text-secondary flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold">You'll hear back soon</h3>
              <p className="text-sm text-muted-foreground">
                The hiring team will review your profile and you'll receive feedback within
                24–48 hours, regardless of the outcome.
              </p>
            </div>
          </div>
        </div>

        {token && (
          <div className="border border-border rounded-lg p-5 text-left mb-6">
            {submitted || alreadySubmitted ? (
              <div className="flex flex-col items-center py-2 gap-2 text-center" data-testid="feedback-submitted">
                <ThumbsUp className="w-8 h-8 text-primary" />
                <p className="font-semibold">Thanks for your feedback!</p>
                <p className="text-sm text-muted-foreground">
                  Your response helps us improve the experience.
                </p>
              </div>
            ) : (
              <>
                <p className="font-semibold mb-1">How was your experience?</p>
                <p className="text-sm text-muted-foreground mb-4">
                  Rate this interview experience (optional but appreciated).
                </p>

                <div className="flex gap-1 mb-4" data-testid="star-rating">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      data-testid={`star-${star}`}
                      onClick={() => setRating(star)}
                      onMouseEnter={() => setHovered(star)}
                      onMouseLeave={() => setHovered(0)}
                      className="p-1 transition-transform hover:scale-110"
                      aria-label={`Rate ${star} star${star !== 1 ? "s" : ""}`}
                    >
                      <Star
                        className={`w-7 h-7 transition-colors ${
                          star <= (hovered || rating)
                            ? "fill-yellow-400 text-yellow-400"
                            : "text-muted-foreground"
                        }`}
                      />
                    </button>
                  ))}
                </div>

                <Textarea
                  data-testid="input-feedback-comment"
                  placeholder="Any comments? (optional)"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  className="mb-3 resize-none"
                  rows={3}
                  maxLength={1000}
                />

                {feedbackError && (
                  <p className="text-sm text-destructive mb-3" data-testid="feedback-error">
                    {feedbackError}
                  </p>
                )}

                <Button
                  data-testid="button-submit-feedback"
                  onClick={submitFeedback}
                  disabled={rating === 0 || submitting}
                  className="w-full"
                  variant="outline"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    "Submit Feedback"
                  )}
                </Button>

                {rating === 0 && (
                  <p className="text-xs text-muted-foreground mt-2 text-center">
                    Select a star rating to submit.
                  </p>
                )}
              </>
            )}
          </div>
        )}

        <p className="text-xs text-muted-foreground">
          Questions? Contact the hiring team directly.
        </p>
      </Card>
    </div>
  );
}
